<script setup lang="ts">
import { computed, useTemplateRef, inject } from "vue";
import routeManager from "@/router/route-manager";
import { useMenuPermissions } from "@/composables/menu-permissions";
import { useUserStore } from "@/stores/User";
import { storeToRefs } from "pinia";
import SignoutModal from "@/components/modals/SignoutModal.vue";
import type { LabelService } from "@/composables/label-service";
import { labelServiceSymbol } from "@/plugins/LabelServicePlugin";

const userStore = useUserStore();

const labelService = inject<LabelService>( labelServiceSymbol ) as LabelService;

const {
  showMyLibrary,
  showProfileDropdown,
} = useMenuPermissions();
const { userFullName } = storeToRefs( userStore );

function hideMenuOnBlur( event: MouseEvent ) {
  if ( event.target ) {
    ( event.target as Element ).removeAttribute( "open" );
  }
}

function showMenuOnFocus( event: Event ) {
  if ( event.target ) {
    ( event.target as Element ).setAttribute( "open", "" );
  }
}

const signoutModalRef = useTemplateRef('signoutModal');
function showSignoutModal() {
  signoutModalRef.value?.open();
}

const recurlyHostedLink = computed< string | undefined >( () => userStore.recurlyHostedLink );
function onOpenRecurly() {
  window.open( recurlyHostedLink.value, '_blank' );
}
</script>

<template>
  <details @mouseenter="showMenuOnFocus" @mouseleave="hideMenuOnBlur" class="header-menu dropdown dropdown-end dropdown-hover">
    <summary class="btn btn-sm btn-ghost text-base">
      <i class="text-primary pi pi-user lg:inline"></i>
      <span class="hidden lg:inline items-center gap-2 text-base">
        {{ userFullName }}
      </span>
    </summary>
    <ul class="menu dropdown-content w-min-58" @click="hideMenuOnBlur">
      <li v-if="showProfileDropdown">
        <router-link :to="routeManager.profileBasePath()">
          <i class="text-primary pi pi-user"></i>
          <span class="ml-2 text-base">{{ labelService.label( 'lblProfile' ) }}</span>
        </router-link>
      </li>
      <li v-if="showMyLibrary">
        <router-link :to="routeManager.myLibraryPath()">
          <i class="text-primary pi pi-book"></i>
          <span class="ml-2 text-base">{{ labelService.label( 'lblMyLibrary' ) }}</span>
        </router-link>
      </li>
      <li v-if="recurlyHostedLink">
        <div class="flex items-center w-full gap-2 text-base font-semibold" @click.prevent="onOpenRecurly">
          <i class="text-primary pi pi-credit-card"></i>
          <span class="ml-2 text-base">{{ labelService.label( 'lblBilling' ) }}</span>
        </div>
      </li>
      <li>
        <div class="flex items-center w-full gap-2 text-base font-semibold" @click.prevent="showSignoutModal">
          <i class="text-primary pi pi-sign-out"></i>
          <span class="ml-2 text-base">{{ labelService.label( 'lblSignOut' ) }}</span>
        </div>
      </li>
    </ul>
  </details>

<SignoutModal ref="signoutModal"></SignoutModal>

</template>
